import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  defaultLayout,
  isNarrow,
  KEYS,
  type Layout,
  type NamedLayout,
  normalize,
  readSaved,
  readStorage,
  writeStorage,
} from "./layout";

interface LayoutApi {
  layout: Layout;
  update: (change: (draft: Layout) => Layout) => void;
  saved: NamedLayout[];
  activeId: string | null;
  /** True when the current layout differs from the saved one it was applied from. */
  modified: boolean;
  saveAs: (name: string) => void;
  overwrite: (id: string) => void;
  rename: (id: string, name: string) => void;
  remove: (id: string) => void;
  apply: (id: string) => void;
  reset: () => void;
}

const LayoutContext = createContext<LayoutApi | null>(null);

const same = (a: Layout, b: Layout) => JSON.stringify(a) === JSON.stringify(b);

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export function LayoutProvider({ children }: { children: ReactNode }) {
  const narrow = isNarrow();
  const [layout, setLayout] = useState<Layout>(() => normalize(readStorage(KEYS.current), narrow));
  const [saved, setSaved] = useState<NamedLayout[]>(() => readSaved(narrow));
  const [activeId, setActiveId] = useState<string | null>(() => {
    const id = readStorage(KEYS.active);
    return typeof id === "string" ? id : null;
  });

  useEffect(() => writeStorage(KEYS.current, layout), [layout]);
  useEffect(() => writeStorage(KEYS.saved, saved), [saved]);
  useEffect(() => writeStorage(KEYS.active, activeId), [activeId]);

  const update = useCallback((change: (draft: Layout) => Layout) => {
    setLayout((current) => change(current));
  }, []);

  const api = useMemo<LayoutApi>(() => {
    const active = saved.find((s) => s.id === activeId) ?? null;
    return {
      layout,
      update,
      saved,
      activeId: active?.id ?? null,
      modified: active ? !same(active.layout, layout) : false,
      saveAs(name) {
        const id = newId();
        setSaved((list) => [
          ...list,
          { id, name: name.trim() || "Untitled", savedAt: new Date().toISOString(), layout },
        ]);
        setActiveId(id);
      },
      overwrite(id) {
        setSaved((list) =>
          list.map((s) => (s.id === id ? { ...s, layout, savedAt: new Date().toISOString() } : s)),
        );
        setActiveId(id);
      },
      rename(id, name) {
        setSaved((list) =>
          list.map((s) => (s.id === id ? { ...s, name: name.trim() || s.name } : s)),
        );
      },
      remove(id) {
        setSaved((list) => list.filter((s) => s.id !== id));
        if (activeId === id) setActiveId(null);
      },
      apply(id) {
        const found = saved.find((s) => s.id === id);
        if (!found) return;
        setLayout(found.layout);
        setActiveId(id);
      },
      reset() {
        setLayout(defaultLayout(isNarrow()));
        setActiveId(null);
      },
    };
  }, [layout, saved, activeId, update]);

  return <LayoutContext.Provider value={api}>{children}</LayoutContext.Provider>;
}

export function useLayout(): LayoutApi {
  const value = useContext(LayoutContext);
  if (!value) throw new Error("useLayout outside LayoutProvider");
  return value;
}

/** Applies the theme preference to the document so CSS tokens switch. */
export function useThemeEffect() {
  const { layout } = useLayout();
  const theme = layout.preferences.theme;
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "auto") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
  }, [theme]);
}
